import React from "react";
import { useDispatch } from "react-redux";
import { logout } from "../features/auth/authSlice";
import { useNavigate } from "react-router-dom";

export function ApiErrorHandler({ error, onRetry, onLogout }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const handleLogout = () => {
    dispatch(logout());
    navigate("/login");
    if (onLogout) onLogout();
  };

  // Check if it's a CORS or network error
  const isCorsError = error?.status === "FETCH_ERROR" || 
                      error?.error?.includes?.("CORS") ||
                      error?.error?.includes?.("Failed to fetch");

  const isAuthError = error?.status === 401 || error?.status === 403;

  if (isCorsError) {
    return (
      <div style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "400px",
        padding: "24px",
      }}>
        <div style={{
          maxWidth: "500px",
          background: "white",
          borderRadius: "16px",
          border: "2px solid #FEE2E2",
          padding: "32px",
          textAlign: "center",
        }}>
          <div style={{
            width: "64px",
            height: "64px",
            borderRadius: "50%",
            background: "#FEE2E2",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            margin: "0 auto 24px",
            fontSize: "2rem",
          }}>
            ⚠️
          </div>
          <h2 style={{
            fontSize: "1.5rem",
            fontWeight: 700,
            color: "#DC2626",
            marginBottom: "12px",
          }}>
            Connection Error
          </h2>
          <p style={{
            fontSize: "0.9375rem",
            color: "#64748B",
            lineHeight: 1.6,
            marginBottom: "24px",
          }}>
            Unable to connect to the API server. This could be because the backend server is not running or there's a network issue.
          </p>
          <p style={{
            fontSize: "0.875rem",
            color: "#94A3B8",
            marginBottom: "24px",
            fontFamily: "monospace",
            background: "#F8FAFC",
            padding: "12px",
            borderRadius: "8px",
          }}>
            {error?.error || "Network error"}
          </p>
          <div style={{
            display: "flex",
            gap: "12px",
            justifyContent: "center",
          }}>
            {onRetry && (
              <button
                onClick={onRetry}
                style={{
                  padding: "12px 24px",
                  border: "1px solid #E2E8F0",
                  background: "white",
                  borderRadius: "8px",
                  fontSize: "0.875rem",
                  fontWeight: 600,
                  cursor: "pointer",
                  color: "#64748B",
                }}
              >
                Retry
              </button>
            )}
            <button
              onClick={handleLogout}
              style={{
                padding: "12px 24px",
                border: "none",
                background: "linear-gradient(135deg, #DC2626 0%, #B91C1C 100%)",
                color: "white",
                borderRadius: "8px",
                fontSize: "0.875rem",
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              Logout & Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (isAuthError) {
    return (
      <div style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "400px",
        padding: "24px",
      }}>
        <div style={{
          maxWidth: "500px",
          background: "white",
          borderRadius: "16px",
          border: "2px solid #FEF3C7",
          padding: "32px",
          textAlign: "center",
        }}>
          <div style={{
            width: "64px",
            height: "64px",
            borderRadius: "50%",
            background: "#FEF3C7",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            margin: "0 auto 24px",
            fontSize: "2rem",
          }}>
            🔒
          </div>
          <h2 style={{
            fontSize: "1.5rem",
            fontWeight: 700,
            color: "#F59E0B",
            marginBottom: "12px",
          }}>
            {error?.status === 401 ? "Session Expired" : "Access Denied"}
          </h2>
          <p style={{
            fontSize: "0.9375rem",
            color: "#64748B",
            lineHeight: 1.6,
            marginBottom: "24px",
          }}>
            {error?.status === 401
              ? "Your session has expired. Please login again to continue."
              : "You don't have permission to access this resource."}
          </p>
          <button
            onClick={handleLogout}
            style={{
              padding: "12px 32px",
              border: "none",
              background: "linear-gradient(135deg, #F5B82E 0%, #D4A029 100%)",
              color: "white",
              borderRadius: "8px",
              fontSize: "0.9375rem",
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            Login Again
          </button>
        </div>
      </div>
    );
  }

  // Generic error
  return (
    <div style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      minHeight: "400px",
      padding: "24px",
    }}>
      <div style={{
        maxWidth: "500px",
        background: "white",
        borderRadius: "16px",
        border: "2px solid #E2E8F0",
        padding: "32px",
        textAlign: "center",
      }}>
        <div style={{
          width: "64px",
          height: "64px",
          borderRadius: "50%",
          background: "#F1F5F9",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          margin: "0 auto 24px",
          fontSize: "2rem",
        }}>
          ❌
        </div>
        <h2 style={{
          fontSize: "1.5rem",
          fontWeight: 700,
          color: "#14213D",
          marginBottom: "12px",
        }}>
          Something Went Wrong
        </h2>
        <p style={{
          fontSize: "0.9375rem",
          color: "#64748B",
          lineHeight: 1.6,
          marginBottom: "24px",
        }}>
          {error?.data?.message || error?.message || "An unexpected error occurred. Please try again."}
        </p>
        <div style={{
          display: "flex",
          gap: "12px",
          justifyContent: "center",
        }}>
          {onRetry && (
            <button
              onClick={onRetry}
              style={{
                padding: "12px 24px",
                border: "1px solid #E2E8F0",
                background: "white",
                borderRadius: "8px",
                fontSize: "0.875rem",
                fontWeight: 600,
                cursor: "pointer",
                color: "#64748B",
              }}
            >
              Retry
            </button>
          )}
          <button
            onClick={handleLogout}
            style={{
              padding: "12px 24px",
              border: "none",
              background: "linear-gradient(135deg, #64748B 0%, #475569 100%)",
              color: "white",
              borderRadius: "8px",
              fontSize: "0.875rem",
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            Logout
          </button>
        </div>
      </div>
    </div>
  );
}
